/** Formatting: integer paise, quantities, decimals-as-strings and IST instants (ruling 0.3, 0.9). */

export const NOT_AVAILABLE = "—";

const RUPEES = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const COUNT = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const IST = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
  timeZoneName: "short",
});
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL = /^-?\d+(\.\d+)?$/;
const PLACES = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function toBigInt(value: bigint | number | string): bigint {
  if (typeof value === "number" && !Number.isSafeInteger(value)) {
    throw new RangeError("an amount must be an integer");
  }
  return BigInt(value);
}

/** Format integer paise as rupees with Indian grouping, exactly (no floating point). */
export function formatPaise(value: bigint | number | string): string {
  const paise = toBigInt(value);
  const negative = paise < 0n;
  const magnitude = negative ? -paise : paise;
  const rupees = magnitude / 100n;
  const fraction = (magnitude % 100n).toString().padStart(2, "0");
  const text = `${negative ? "-" : ""}${rupees.toString()}.${fraction}` as `${number}`;
  return RUPEES.format(text);
}

/** Format a whole quantity with Indian grouping. */
export function formatQuantity(value: bigint | number): string {
  return COUNT.format(toBigInt(value));
}

/** Show a derived decimal string verbatim, checking its declared scale (digits after the point). */
export function formatDecimal(text: string, scale: number): string {
  const digits = text.includes(".") ? text.length - text.indexOf(".") - 1 : 0;
  if (!DECIMAL.test(text) || digits !== scale) {
    throw new RangeError(`a decimal of scale ${String(scale)} was expected`);
  }
  return text;
}

/** Format parts-per-million as a percentage with one decimal, by integer arithmetic. */
export function formatPpm(ppm: number): string {
  const tenths = Math.trunc(Math.abs(ppm) / 1000);
  return `${ppm < 0 ? "-" : ""}${String(Math.trunc(tenths / 10))}.${String(tenths % 10)}%`;
}

/** Format basis points as a percentage with two decimals, by integer arithmetic. */
export function formatBasisPoints(bp: number): string {
  const magnitude = Math.abs(bp);
  const hundredths = String(magnitude % 100).padStart(2, "0");
  return `${bp < 0 ? "-" : ""}${String(Math.trunc(magnitude / 100))}.${hundredths}%`;
}

/** Parse a UTC instant (`...ffffffZ`) into microseconds since the epoch. */
export function parseInstant(text: string): bigint {
  if (!INSTANT.test(text)) {
    throw new RangeError("not a UTC instant with microseconds");
  }
  const part = (from: number, to: number): number => Number(text.slice(from, to));
  const millis = Date.UTC(
    part(0, 4),
    part(5, 7) - 1,
    part(8, 10),
    part(11, 13),
    part(14, 16),
    part(17, 19),
  );
  return BigInt(millis) * 1000n + BigInt(part(20, 26));
}

/** Format a UTC instant in Asia/Kolkata; the label `IST` always follows (WC-27). */
export function formatInstant(text: string): string {
  const millis = Number(parseInstant(text) / 1000n);
  return IST.formatToParts(new Date(millis))
    .map((part) => (part.type === "timeZoneName" ? "IST" : part.value))
    .join("");
}

/** Format an exchange date (`YYYY-MM-DD`) with its zone label. */
export function formatExchangeDate(text: string): string {
  if (!DATE.test(text)) {
    throw new RangeError("not an exchange date");
  }
  const part = (from: number, to: number): number => Number(text.slice(from, to));
  return `${PLACES.format(new Date(Date.UTC(part(0, 4), part(5, 7) - 1, part(8, 10))))} IST`;
}

/** Format a duration in microseconds as seconds or minutes, by integer arithmetic. */
export function formatDurationUs(us: number): string {
  const millis = Math.trunc(us / 1000);
  if (millis < 60_000) {
    return `${String(Math.trunc(millis / 1000))}.${String(Math.trunc((millis % 1000) / 100))} s`;
  }
  const seconds = Math.trunc(millis / 1000);
  return `${String(Math.trunc(seconds / 60))} min ${String(seconds % 60).padStart(2, "0")} s`;
}

/** Format a value that may be absent: `null` is a dash, never zero (UX07). */
export function formatNullable<T>(value: T | null, format: (present: T) => string): string {
  return value === null ? NOT_AVAILABLE : format(value);
}
